import { useCallback, useEffect, useRef, useState } from "react";
import {
  startListenerCompetitorAdFavorites,
  startRemoveCompetitorAdFavorite,
  startSetCompetitorAdFavorite,
  stopListenerCompetitorAdFavorites,
} from "../actions/competitorAdsFavorites";
import { isCompetitorAdFavorite } from "../services/metaAdLibrary";

const getFavoriteToggleKey = ({ pageId, libraryID }) =>
  `${pageId}/${libraryID}`;

export const useCompetitorAdFavorites = ({ modalOpen, dispatch }) => {
  const [favoritesByPageId, setFavoritesByPageId] = useState(null);
  const [togglingAdKeys, setTogglingAdKeys] = useState(() => new Set());
  const listenerRef = useRef(null);

  useEffect(() => {
    if (!modalOpen) {
      setFavoritesByPageId(null);
      return undefined;
    }

    dispatch(
      startListenerCompetitorAdFavorites({
        onUpdatedDataCallback: setFavoritesByPageId,
        onReferenceAvailable: (dbRef) => {
          listenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCompetitorAdFavorites({ listenerRef: listenerRef.current })
      );
      listenerRef.current = null;
    };
  }, [dispatch, modalOpen]);

  const isFavorite = useCallback(
    ({ pageId, libraryID, item = null }) => {
      return isCompetitorAdFavorite({
        favoritesMap: favoritesByPageId,
        pageId,
        libraryID,
        item,
      });
    },
    [favoritesByPageId]
  );

  const isTogglingFavorite = useCallback(
    ({ pageId, libraryID }) => {
      return togglingAdKeys.has(getFavoriteToggleKey({ pageId, libraryID }));
    },
    [togglingAdKeys]
  );

  const onToggleFavorite = useCallback(
    async ({ item }) => {
      const pageId = item?.pageId;
      const libraryID = item?.libraryID ?? item?.id;
      const toggleKey = getFavoriteToggleKey({ pageId, libraryID });

      if (!pageId || !libraryID || !item?.url) return;

      const wasFavorite = isCompetitorAdFavorite({
        favoritesMap: favoritesByPageId,
        pageId,
        libraryID,
        item,
      });

      setTogglingAdKeys((current) => new Set(current).add(toggleKey));

      try {
        const response = wasFavorite
          ? await dispatch(
              startRemoveCompetitorAdFavorite({ pageId, libraryID })
            )
          : await dispatch(
              startSetCompetitorAdFavorite({ pageId, libraryID, ad: item })
            );

        return response;
      } finally {
        setTogglingAdKeys((current) => {
          const next = new Set(current);
          next.delete(toggleKey);
          return next;
        });
      }
    },
    [dispatch, favoritesByPageId]
  );

  return {
    favoritesByPageId,
    isFavorite,
    isTogglingFavorite,
    onToggleFavorite,
  };
};
